const axios = require("axios");
const axiosRetry = require("axios-retry");
const MockAdapter = require("axios-mock-adapter");

const ALLOWED_HTTP_METHODS = [
  "delete",
  "get",
  "head",
  "patch",
  "post",
  "put",
  "request",
];

class API {
  constructor(origin = "", errorHandler, axiosConfig, responseHandler) {
    this.origin = origin;
    this.errorHandler = errorHandler;

    const cancelToken = {
      cancelToken: new axios.CancelToken(c => {
        this.cancelFn = c;
      })
    };
    this.axiosConfig = { ...cancelToken, ...axiosConfig };

    this.responseHandler = responseHandler;
  }

  cancel(message) {
    this.cancelFn(message);
  }

  async do(axiosFn, path, data, callbackError) {
    // Additional instances object for the axios retry
    axiosRetry(axios, {
      retries: 4,
      retryDelay: axiosRetry.exponentialDelay,
      retryCondition: (error) => {
        //Allow post and patch method to be retriable when error occurred
        return ['post', 'patch'].includes(error.config.method) || axiosRetry.isNetworkOrIdempotentRequestError(error);
      }
    });
    try {
      const response = data
        ? await axiosFn(this.origin + path, data, this.axiosConfig)
        : await axiosFn(this.origin + path, this.axiosConfig);
      return this.responseHandler ? this.responseHandler(response) : response;
    } catch (error) {
      if (!axios.isCancel(error)) {
        const { config, message, request, response } = error;

        console.error("Exception invoking:\n", `${this.origin}${path}`);
        console.error("Error config:\n", config);

        // Taken from https://github.com/axios/axios#handling-errors
        if (response) {
          // The request was made and the server responded with
          // a status code that falls out of the range of 2xx
          console.error("Error response:\n", response);

          if (this.errorHandler) {
            this.errorHandler(response.data);
          }

          if (callbackError) {
            callbackError(response.data);
          }
        } else if (request) {
          // The request was made but no response was received
          // "request" is an instance of XMLHttpRequest in the
          // browser and an instance of http.ClientRequest in node.js
          console.error("No response was received for the request:\n", request);
        } else {
          // Something happened in setting up the request that triggered an Error
          console.error("Error setting up the request:\n", message);
        }
      }

      return null;
    }
  }

  request(path, callbackError) {
    return this.do(axios.request, path, callbackError);
  }

  get(path, callbackError) {
    return this.do(axios.get, path, null, callbackError);
  }

  delete(path, callbackError) {
    return this.do(axios.delete, path, callbackError);
  }

  head(path, callbackError) {
    return this.do(axios.head, path, callbackError);
  }

  post(path, data, callbackError) {
    return this.do(axios.post, path, data, callbackError);
  }

  put(path, data, callbackError) {
    return this.do(axios.put, path, data, callbackError);
  }

  patch(path, data, callbackError) {
    return this.do(axios.patch, path, data, callbackError);
  }
}

const basicAPI = (origin) => {
  return new API(origin);
}

const apiWithLoader = async ({
  config,
  errorHandler,
  isStateObject = false,
  loadingProperty,
  loadingCallback,
}) => {
  const {
    data,
    headers,
    method: configMethod = "",
    path = "",
    url,
  } = config;

  const method = configMethod.toLowerCase();

  if (!ALLOWED_HTTP_METHODS.includes(method)) {
    console.error(
      `HTTP method ${configMethod} is not allowed. Allowed methods are ${ALLOWED_HTTP_METHODS.toString()}`
    );
    return null;
  }

  const api = new API(url, errorHandler, { headers });

  const setLoading = (isLoading) => {
    if (!loadingCallback) {
      return;
    }

    if (isStateObject && !loadingProperty) {
      return;
    }

    if (isStateObject) {
      loadingCallback((state) => ({
        ...state,
        [loadingProperty]: isLoading,
      }));
    } else {
      loadingCallback(isLoading);
    }
  };

  setLoading(true);

  const response = ["post", "put", "patch"].includes(method)
    ? await api[method](path, data)
    : await api[method](path);

  setLoading(false);

  return response;
};

const mockRequest = (mockData, mockCondition) => {
  const mockAdapter = new MockAdapter(axios);

  if (mockCondition) {
    mockAdapter.onAny().reply(mockData);
  } else {
    mockAdapter.onAny().passThrough();
  }
}

module.exports = {
  API,
  apiWithLoader,
  basicAPI,
  mockRequest
}